/* RESPONSIBLE TEAM: team-standalone */

import RSVP from 'rsvp';
import { taskFor } from 'ember-concurrency-ts';
import { dropTask, restartableTask } from 'ember-concurrency-decorators';
import { get } from 'embercom/lib/ajax';
import { type TaskGenerator } from 'ember-concurrency';
import type Store from '@ember-data/store';
import type IntlService from 'ember-intl/services/intl';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type {
  SalesforceAdmin,
  SalesforceQueue,
  SalesforceSupportEmail,
} from 'embercom/objects/standalone/salesforce';
import {
  TicketField,
  type TicketFieldResponse,
  UserField,
  type UserFieldResponse,
} from 'embercom/objects/standalone/data-fields/common';
import { SalesforceDataFields } from 'embercom/objects/standalone/data-fields/salesforce';

export default class SalesforceDataLoader {
  private finStandaloneService: FinStandaloneService;

  constructor(finStandaloneService: FinStandaloneService) {
    this.finStandaloneService = finStandaloneService;
  }

  get app() {
    return this.finStandaloneService.app;
  }

  get intl(): IntlService {
    return this.finStandaloneService.intl;
  }

  get notificationsService() {
    return this.finStandaloneService.notificationsService;
  }

  get store(): Store {
    return this.finStandaloneService.store;
  }

  get isLoadingSalesforceConfig() {
    return taskFor(this.loadSalesforceConfig).isRunning;
  }

  get isLoadingSalesforceImportOnlyConfig() {
    return taskFor(this.loadSalesforceImportOnlyConfig).isRunning;
  }

  get isLoadingSalesforceSetupData() {
    return taskFor(this.loadSalesforceSetupData).isRunning;
  }

  get isLoadingSalesforceInAppMessagingConfig() {
    return taskFor(this.loadSalesforceInAppMessagingConfig).isRunning;
  }

  get isLoadingSalesforceLiveChatConfig() {
    return taskFor(this.loadSalesforceLiveChatConfig).isRunning;
  }

  get isLoadingSalesforceDataFields() {
    return taskFor(this.loadSalesforceDataFields).isRunning;
  }

  get isLoadingSalesforcePermissions() {
    return taskFor(this.checkSalesforcePermissions).isRunning;
  }

  get isLoadingSalesforcePermissionsHealthcheck() {
    return taskFor(this.loadSalesforcePermissionsHealthcheck).isRunning;
  }

  async performLoads() {
    let promises: Promise<unknown>[] = [
      taskFor(this.loadSalesforceConfig).perform(),
      taskFor(this.loadSalesforceImportOnlyConfig).perform(),
      this.store.findAll('standalone/external-field-identifier'),
    ];

    await RSVP.all(promises);
  }

  async performLazyLoads() {
    this.app.loadEmailDomains(false);
    taskFor(this.loadSalesforceSetupData).perform();
    taskFor(this.loadSalesforceInAppMessagingConfig).perform();
    taskFor(this.loadSalesforceLiveChatConfig).perform();
    taskFor(this.loadSalesforceDataFields).perform();
  }

  @dropTask *loadSalesforceConfig() {
    try {
      this.finStandaloneService.salesforceConfig = yield this.store.findRecord(
        'standalone/salesforce-configuration',
        this.app.id,
      );

      if (!this.finStandaloneService.salesforceConfig) {
        this.finStandaloneService.salesforceConfig = this.store.createRecord(
          'standalone/salesforce-configuration',
          {
            id: this.app.id,
          },
        );
      }
    } catch (e) {
      console.error(e.message || e.jqXHR);
      if (e.jqXHR?.status !== 404) {
        throw e;
      }
    }
  }

  @dropTask *loadSalesforceImportOnlyConfig(): TaskGenerator<void> {
    // TODO Replace this with usage of Ember Data?
    let salesforceImportOnlyConfig = yield get(
      `/ember/standalone/salesforce_configurations/${this.app.id}/find_import_only_integration`,
      {
        app_id: this.app.id,
      },
    ).catch(() => ({}));

    if (salesforceImportOnlyConfig?.instance_url) {
      this.finStandaloneService.salesforceImportOnlyConfig = {
        instanceUrl: salesforceImportOnlyConfig.instance_url,
        organizationId: salesforceImportOnlyConfig.organization_id,
        isSandbox: salesforceImportOnlyConfig.is_sandbox,
      };
    }
  }

  @dropTask *loadSalesforceSetupData(): TaskGenerator<void> {
    if (this.finStandaloneService.salesforceConfig?.isPendingSetup) {
      this.finStandaloneService.salesforceSetupData = {
        admins: [],
        queues: [],
        caseOrigins: [],
        supportEmails: [],
        permissionsHealthcheck: [],
        hasPermissionSet: undefined,
        hasPermissionSetAssignedToInstaller: undefined,
      };

      return;
    }

    // TODO Replace this with usage of Ember Data
    let salesforceAdmins = yield get(
      `/ember/standalone/salesforce_configurations/${this.app.id}/list_salesforce_users`,
      {
        app_id: this.app.id,
      },
    ).catch(() => []);

    let salesforceQueues = yield get(
      `/ember/standalone/salesforce_configurations/${this.app.id}/list_salesforce_queues`,
      {
        app_id: this.app.id,
      },
    ).catch(() => []);

    let salesforceCaseOrigins = yield get(
      `/ember/standalone/salesforce_configurations/${this.app.id}/list_salesforce_case_origins`,
      {
        app_id: this.app.id,
      },
    ).catch(() => []);

    let selectedCaseOrigins = yield get(
      `/ember/standalone/salesforce_configurations/${this.app.id}/list_selected_case_origins`,
      {
        app_id: this.app.id,
      },
    );

    let salesforceSupportEmails = yield get(
      `/ember/standalone/salesforce_configurations/${this.app.id}/list_support_emails`,
      {
        app_id: this.app.id,
      },
    ).catch(() => []);

    let checkPermissions = yield get(
      `/ember/standalone/salesforce_configurations/${this.app.id}/check_permissions`,
      {
        app_id: this.app.id,
      },
    ).catch(() => ({}));

    let permissionsHealthcheck = yield get('/ember/standalone/healthcheck', {
      app_id: this.app.id,
    }).catch(() => []);

    this.finStandaloneService.selectedSalesforceCaseOrigins = selectedCaseOrigins;

    this.finStandaloneService.salesforceSetupData = {
      admins: salesforceAdmins as Array<SalesforceAdmin>,
      queues: salesforceQueues as Array<SalesforceQueue>,
      caseOrigins: salesforceCaseOrigins.sort() as Array<string | { value: string; label: string }>,
      supportEmails: salesforceSupportEmails as Array<SalesforceSupportEmail>,
      permissionsHealthcheck,
      hasPermissionSet: checkPermissions.has_permission_set,
      hasPermissionSetAssignedToInstaller:
        checkPermissions.has_permission_set_assigned_to_installer,
    };
  }

  @dropTask *loadSalesforceInAppMessagingConfig(): TaskGenerator<void> {
    if (this.finStandaloneService.salesforceConfig?.isPendingSetup) {
      return;
    }

    try {
      this.finStandaloneService.salesforceInAppMessagingConfig = yield this.store.findRecord(
        'standalone/salesforce-in-app-messaging-configuration',
        this.app.id,
      );
    } catch (e) {
      console.error(e.message || e.jqXHR);
      if (e.jqXHR?.status !== 404) {
        throw e;
      }
    }
  }

  @dropTask *loadSalesforceSupportEmails(): TaskGenerator<void> {
    if (this.finStandaloneService.salesforceConfig?.isPendingSetup) {
      return;
    }

    let supportEmails = yield get(
      `/ember/standalone/salesforce_configurations/${this.app.id}/list_support_emails`,
      {
        app_id: this.app.id,
      },
    ).catch(() => []) as Array<SalesforceSupportEmail>;

    this.finStandaloneService.salesforceSetupData = {
      ...this.finStandaloneService.salesforceSetupData,
      supportEmails,
    };
  }

  @dropTask *loadSalesforceLiveChatConfig(): TaskGenerator<void> {
    if (
      !this.app.canUseHandoffToSalesforceChat ||
      this.finStandaloneService.salesforceConfig?.isPendingSetup
    ) {
      return;
    }

    try {
      this.finStandaloneService.salesforceLiveChatConfig = yield this.store.findRecord(
        'standalone/salesforce-live-chat-configuration',
        this.app.id,
      );
    } catch (e) {
      console.error(e.message || e.jqXHR);
      if (e.jqXHR?.status !== 404) {
        throw e;
      }
    }
  }

  @dropTask *loadSalesforceDataFields(): TaskGenerator<void> {
    yield this.reloadSalesforceDataFields();
  }

  @dropTask *loadSalesforcePermissionsHealthcheck(): TaskGenerator<void> {
    if (this.finStandaloneService.salesforceConfig?.isPendingSetup) {
      return;
    }

    let permissionsHealthcheck = yield get('/ember/standalone/healthcheck', {
      app_id: this.app.id,
    }).catch(() => []);

    this.finStandaloneService.salesforceSetupData = {
      ...this.finStandaloneService.salesforceSetupData,
      permissionsHealthcheck,
    };
  }

  async reloadSalesforceDataFields() {
    if (this.finStandaloneService.salesforceConfig?.isPendingSetup) {
      this.finStandaloneService.salesforceDataFields = new SalesforceDataFields({
        userFields: [],
        caseFields: [],
        cvdasForRequiredSalesforceCaseFields: [],
      });
      return;
    }

    // TODO Replace this with usage of Ember Data
    let dataFields = (await get(`/ember/standalone/salesforce_data/available_fields`, {
      app_id: this.app.id,
      admin_id: this.app.currentAdmin.id,
    })) as {
      user_fields: UserFieldResponse[];
      case_fields: TicketFieldResponse[];
      cvdas_for_required_salesforce_case_fields: {
        intercom_cvda: { id: string };
        salesforce_field_id: string;
      }[];
    };

    this.finStandaloneService.salesforceDataFields = new SalesforceDataFields({
      userFields: dataFields.user_fields.map((field) => new UserField(field)),
      caseFields: dataFields.case_fields.map((field) => new TicketField(field)),
      cvdasForRequiredSalesforceCaseFields: dataFields.cvdas_for_required_salesforce_case_fields,
    });
  }

  @restartableTask *checkSalesforcePermissions(): TaskGenerator<void> {
    let { has_permission_set, has_permission_set_assigned_to_installer } = yield get(
      `/ember/standalone/salesforce_configurations/${this.app.id}/check_permissions`,
      {
        app_id: this.app.id,
      },
    ).catch(() => ({
      has_permission_set: this.finStandaloneService.salesforceSetupData.hasPermissionSet,
      has_permission_set_assigned_to_installer:
        this.finStandaloneService.salesforceSetupData.hasPermissionSetAssignedToInstaller,
    }));

    if (has_permission_set_assigned_to_installer) {
      // Reload the setup data as we've just assigned the permission set to the installer
      yield taskFor(this.loadSalesforceSetupData).perform();
    } else {
      // Otherwise, just update the setup data with the permission set status
      this.finStandaloneService.salesforceSetupData = {
        ...this.finStandaloneService.salesforceSetupData,
        hasPermissionSet: has_permission_set,
        hasPermissionSetAssignedToInstaller: has_permission_set_assigned_to_installer,
      };
    }
  }

  @restartableTask *hasRequiredSalesforceFinPermissionsTask(): TaskGenerator<void> {
    if (this.finStandaloneService.salesforceConfig?.isPendingSetup) {
      return;
    }

    // TODO Replace this with usage of Ember Data
    let hasPermissions = yield get(
      `/ember/standalone/salesforce_configurations/${this.app.id}/has_required_fin_permissions`,
      {
        app_id: this.app.id,
      },
    );
    this.finStandaloneService.hasPermissionPackageBeenInstalled =
      hasPermissions.has_fin_permissions;
    this.finStandaloneService.hasRequiredSalesforceFinPermissions =
      hasPermissions.has_admin_permission_set;
  }
}
